import React, { useState, useEffect } from "react";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import { Button } from "@/components/ui/Button";
import TextAreaBox from "@/components/ui/TextAreaBox";
import ModalLayout from "@/components/layout/ModalLayout";
import { toast } from "sonner";
import ModalHeader from "@/components/ui/ModalHeader";
import { useEmployeeCodeInsert } from "@/hooks/standard/st_employee_code/useEmployeeCode";
import { useCodeListByDutyCode, useCodeListByTeamCode } from "@/hooks/standard/st_code/useCode";

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EmployeeFormData) => void;
}

export interface EmployeeFormData {
  name: string;
  birthday: string;
  mobileTel: string;
  emergenctTel: string;
  dutycode: string;
  teamcode: string;
  address: string;
  companyId: string;
  isActive: string;
  remark: string;
}

export default function AddModal({ isOpen, onClose, onSave }: AddModalProps) {
  const insertMutation = useEmployeeCodeInsert();
  const { data: dutyData } = useCodeListByDutyCode();
  const { data: teamData } = useCodeListByTeamCode();
  const [formData, setFormData] = useState<EmployeeFormData>({
    name: "",
    birthday: "",
    mobileTel: "",
    emergenctTel: "",
    dutycode: "",
    teamcode: "",
    address: "",
    companyId: "",
    isActive: "true",
    remark: "",
  });

  // 재직 상태 옵션
  const activeStatusOptions = [
    { value: "true", label: "재직" },
    { value: "false", label: "퇴사" },
  ];

  

  const handleInputChange = (field: keyof EmployeeFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // 필수 필드 검증
    if (!formData.name.trim()) {
      toast.warning("이름을 입력해주세요.");
      return;
    }

    // 전화번호 유효성 검사 (선택 사항이지만, 입력된 경우 11자리 숫자인지 확인)
    if (formData.mobileTel.trim()) {
      if (!/^\d+$/.test(formData.mobileTel)) {
        toast.warning("전화번호는 숫자만 입력해주세요.");
        return;
      }
      if (formData.mobileTel.length !== 11) {
        toast.warning("전화번호는 11자리 숫자여야 합니다.");
        return;
      }
    }

    // 비상 연락망 유효성 검사 (선택 사항이지만, 입력된 경우 11자리 숫자인지 확인)
    if (formData.emergenctTel.trim()) {
      if (!/^\d+$/.test(formData.emergenctTel)) {
        toast.warning("비상 연락망은 숫자만 입력해주세요.");
        return;
      }
      if (formData.emergenctTel.length !== 11) {
        toast.warning("비상 연락망은 11자리 숫자여야 합니다.");
        return;
      }
    }

    // 생년월일 유효성 검사 (선택 사항이지만, 입력된 경우 8자리 숫자인지 확인)
    if (formData.birthday.trim()) {
      if (!/^\d+$/.test(formData.birthday)) {
        toast.warning("생년월일은 숫자만 입력해주세요.");
        return;
      }
      if (formData.birthday.length !== 8) {
        toast.warning("생년월일은 8자리 숫자여야 합니다. (YYYYMMDD)");
        return;
      }
    }

    try {
      await insertMutation.mutateAsync(
        {
          name: formData.name,
          birthday: formData.birthday,
          mobileTel: formData.mobileTel,
          emergenctTel: formData.emergenctTel,
          dutycode: formData.dutycode,
          teamcode: formData.teamcode,
          address: formData.address,
          companyId: formData.companyId,
          isActive: formData.isActive === "true" ? true : false,
          remark: formData.remark,
        });

      onSave(formData);
      handleCancel();
      onClose();
    } catch (error) {
      console.error("저장 실패:", error);
    }
  };

  const handleCancel = () => {
    // 폼 데이터 초기화
    setFormData({
      name: "",
      birthday: "",
      mobileTel: "",
      emergenctTel: "",
      dutycode: "",
      teamcode: "",
      address: "",
      companyId: "",
      isActive: "true",
      remark: "",
    });
    onClose();
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={handleCancel} title="사원 코드 추가" maxWidth="max-w-5xl">
      {/* 기본 정보 섹션 */}
      <div className="rounded-lg">
        <ModalHeader title="기본 정보" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputBox
              required={true}
              label="이름"
              value={formData.name}
              onChange={e => handleInputChange("name", e.target.value)}
              placeholder="이름 입력"
              className="w-full"
            />
          </div>

          <div>
            <InputBox
              label="생년월일"
              value={formData.birthday}
              onChange={e => handleInputChange("birthday", e.target.value)}
              placeholder="YYYYMMDD"
              className="w-full"
            />
          </div>

          <div>
            <InputBox
              label="전화번호"
              value={formData.mobileTel}
              onChange={e => handleInputChange("mobileTel", e.target.value)}
              placeholder="01000000000"
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-3">
            <InputBox
              label="주소"
              value={formData.address}
              onChange={e => handleInputChange("address", e.target.value)}
              placeholder="주소 입력"
              className="w-full"
            />
          </div>
        </div>

        <ModalHeader title="추가 정보" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">

          <div>
            <SelectBox
              label="직위"
              value={formData.dutycode}
              onChange={value => handleInputChange("dutycode", value)}
              options={dutyData?.map((item: any) => ({
                value: item.code,
                label: item.name,
              })) || []}
              placeholder="직위 입력"
              className="w-full"
            />
          </div>
          <div>
            <SelectBox
              label="부서"
              value={formData.teamcode}
              onChange={value => handleInputChange("teamcode", value)}
              options={teamData?.map((item: any) => ({
                value: item.code,
                label: item.name,
              })) || []}
              placeholder="부서 입력"
              className="w-full"
            />
          </div>
          <div>
            <SelectBox
              label="재직 상태"
              value={formData.isActive}
              onChange={value => handleInputChange("isActive", value)}
              options={activeStatusOptions}
              placeholder="선택"
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputBox
              label="비상 연락망"
              value={formData.emergenctTel}
              onChange={e => handleInputChange("emergenctTel", e.target.value)}
              placeholder="01000000000"
              className="w-full"
            />
          </div>

          <div className="md:col-span-2">
            <TextAreaBox
              label="비고"
              value={formData.remark}
              onChange={e => handleInputChange("remark", e.target.value)}
              placeholder="비고 입력"
              className="w-full"
              rows={1}
            />
          </div>
        </div>
      </div>

      {/* 모달 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={handleCancel} className="py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full">
          <Button variant="primary" onClick={handleSave} className="py-[10px]">
            저장
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}